import type { BreedRead, PersonRead } from '@pedigreesmart/api-client'
import { attributiSpecie, iri, toFieldValue } from '@pedigreesmart/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession, useTerm } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './animals.css'

/**
 * Un attributo come lo descrive la specie.
 *
 * La forma viene dal contesto di sessione (`/me`), non da un tipo scritto qui:
 * il catalogo lo definisce ogni associazione, e gli attributi di una specie
 * possono cambiare senza che il gestionale venga ricompilato.
 */
interface Attributo {
  key: string
  label: string
  type: string
  group: string | null
  unit: string | null
  options: readonly string[] | null
  required: boolean
}

/**
 * Il modulo per registrare un soggetto.
 *
 * **I campi degli attributi non sono scritti qui.** Li costruisce la
 * definizione della specie, che arriva col contesto di sessione: un cane
 * chiede il microchip, un cavallo lo UELN, un bovino la marca auricolare, e
 * un'associazione che aggiunge un attributo al proprio catalogo lo vede
 * comparire senza che noi tocchiamo niente. Scriverli a mano avrebbe
 * significato una copia da tenere allineata a otto tipi di dato e a un
 * catalogo che non controlliamo.
 *
 * **Quali siano obbligatori lo decide comunque il server.** Qui l'asterisco è
 * un'indicazione: il rifiuto arriva dal validatore, che conosce anche le
 * regole che il modulo non vede.
 *
 * **La specie non si cambia dopo.** È scelta in alto, prima di tutto il
 * resto, perché decide quali campi esistono; cambiarla a modulo compilato
 * svuoterebbe metà delle risposte, quindi il selettore appare solo in
 * creazione.
 */
export function AnimalForm({
  id,
  onFatto,
  onAnnulla,
}: {
  id?: number | undefined
  onFatto: (id: number) => void
  onAnnulla: () => void
}) {
  const { resources, context, species } = useSession()
  const cache = useQueryClient()
  const soggetto = useTerm('animal')
  const modifica = typeof id === 'number'

  const esistente = useQuery({
    queryKey: ['animal', id],
    queryFn: ({ signal }) => resources.animals.get(id as number, signal),
    enabled: modifica,
  })

  const [codiceSpecie, setCodiceSpecie] = useState(species?.code ?? context?.species[0]?.code ?? '')
  const [violazioni, setViolazioni] = useState<Record<string, string>>({})
  const [errore, setErrore] = useState<string | null>(null)
  const [allevatore, setAllevatore] = useState<PersonRead | null>(null)
  // «Toccato» e non solo «scelto»: in modifica un allevatore già presente non
  // va rimandato a ogni salvataggio, e non va nemmeno cancellato per il solo
  // fatto che il modulo non lo ha in memoria. Finché nessuno lo tocca, la
  // chiave si omette e il valore sul server resta quello che è.
  const [allevatoreToccato, setAllevatoreToccato] = useState(false)

  // In modifica la specie è quella del soggetto e non si tocca: decide quali
  // attributi esistono, e cambiarla trasformerebbe il soggetto in un altro.
  const specieAttiva = esistente.data?.species ?? codiceSpecie
  const dato = esistente.data
  const definizione = context?.species.find((s) => s.code === specieAttiva)
  const attributi = (definizione?.attributes ?? []) as readonly Attributo[]

  const razze = useQuery({
    queryKey: ['breeds', specieAttiva],
    queryFn: ({ signal }) => resources.breeds.list(specieAttiva, signal),
    enabled: specieAttiva !== '',
  })

  const salva = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      modifica ? resources.animals.update(id, corpo) : resources.animals.create(corpo),
    onMutate: () => {
      setErrore(null)
      setViolazioni({})
    },
    onSuccess: (animale) => {
      void cache.invalidateQueries({ queryKey: ['animals'] })
      void cache.invalidateQueries({ queryKey: ['animal', animale.id] })
      onFatto(animale.id)
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { propertyPath: string; message: string }[]
      }

      if (problema.status === 422 && problema.violations) {
        // I percorsi degli attributi arrivano come `attributes.microchip`:
        // sono conservati così, perché è la chiave con cui il campo li cerca.
        setViolazioni(
          Object.fromEntries(problema.violations.map((v) => [v.propertyPath, v.message])),
        )
        setErrore('Alcuni dati non vanno bene: sono segnati qui sotto.')

        return
      }

      setErrore(
        problema.status === 403
          ? 'Il vostro profilo non può registrare soggetti.'
          : 'Non è stato possibile salvare.',
      )
    },
  })

  function invia(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const corpo: Record<string, unknown> = {
      foundation_stock: campi.get('foundation_stock') === 'on',
    }

    // La specie si manda solo alla creazione: in modifica è già quella, e
    // rimandarla vorrebbe dire dichiarare di volerla cambiare.
    if (!modifica) corpo['species'] = specieAttiva

    for (const chiave of [
      'name',
      'full_name',
      'sex',
      'birth_date',
      'death_date',
      'color',
      'variety',
      'registry_number',
      'registry_book',
      'registered_on',
      'notes',
    ]) {
      const valore = (campi.get(chiave) as string | null)?.trim() ?? ''

      corpo[chiave] = valore === '' ? null : valore
    }

    // `name` non è annullabile: se manca, si lascia decidere al server, che
    // risponde con la violazione giusta sul campo giusto.
    if (corpo['name'] === null) corpo['name'] = ''

    const razza = (campi.get('breed') as string | null) ?? ''

    // Le relazioni viaggiano come IRI, non come id: un numero produce un
    // `400` che parla di tipi e non dice quale campo sia sbagliato.
    corpo['breed'] = razza === '' ? null : iri('breeds', Number(razza))

    if (!modifica || allevatoreToccato) {
      corpo['breeder'] = iri('people', allevatore?.id)
    }

    // `FormData` può restituire un `File`: un campo di tipo file fra gli
    // attributi darebbe «[object Object]» come valore, cioè un dato falso che
    // al salvataggio verrebbe scritto.
    const grezzi = Object.fromEntries(
      attributi.map((a) => {
        const valore = campi.get(`attr:${a.key}`)

        return [a.key, typeof valore === 'string' ? valore : '']
      }),
    )
    const valori = attributiSpecie(attributi, grezzi)

    corpo['attributes'] = valori

    salva.mutate(corpo)
  }

  if (modifica && esistente.isPending) return <p className="messaggio">Caricamento…</p>

  if (modifica && esistente.isError) {
    return (
      <p role="alert" className="messaggio messaggio--errore">
        Non è stato possibile leggere la scheda da modificare.
      </p>
    )
  }

  const multiSpecie = !modifica && (context?.species.length ?? 0) > 1
  const razzeParziali =
    razze.data !== undefined && razze.data.totalItems > razze.data.member.length

  return (
    <section aria-labelledby="titolo-modulo-soggetto">
      <button type="button" data-variante="piatto" onClick={onAnnulla}>
        ← Annulla
      </button>

      <h1 id="titolo-modulo-soggetto">
        {modifica ? `Modifica ${dato?.full_name ?? dato?.name ?? ''}` : `Nuovo ${soggetto.toLowerCase()}`}
      </h1>

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      <form className="modulo" onSubmit={invia}>
        {multiSpecie && (
          <fieldset>
            <legend>Specie</legend>
            <label className="modulo__campo">
              <span>Specie</span>
              <select
                value={codiceSpecie}
                onChange={(evento) => {
                  setCodiceSpecie(evento.target.value)
                  // Gli attributi cambiano del tutto: tenere le violazioni
                  // vecchie segnerebbe campi che non esistono più.
                  setViolazioni({})
                }}
              >
                {context?.species.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
        )}

        <fieldset>
          <legend>Identità</legend>
          <Campo nome="name" etichetta="Nome" predefinito={dato?.name} violazioni={violazioni} obbligatorio />
          <Campo
            nome="full_name"
            etichetta="Nome completo con affisso"
            predefinito={dato?.full_name}
            violazioni={violazioni}
          />
          <label className="modulo__campo">
            <span>
              Sesso <abbr title="obbligatorio">*</abbr>
            </span>
            <select name="sex" defaultValue={dato?.sex ?? 'U'}>
              <option value="M">Maschio</option>
              <option value="F">Femmina</option>
              {/* «Non determinato» esiste perché i registri storici lo hanno:
                  toglierlo renderebbe impossibile importarli senza inventare
                  un sesso che nessuno ha mai osservato. */}
              <option value="U">Non determinato</option>
            </select>
          </label>
          <Campo nome="birth_date" etichetta="Data di nascita" tipo="date" predefinito={dato?.birth_date} violazioni={violazioni} />
          <Campo nome="death_date" etichetta="Data di morte" tipo="date" predefinito={dato?.death_date} violazioni={violazioni} />
          <Campo nome="color" etichetta="Colore" predefinito={dato?.color} violazioni={violazioni} />
        </fieldset>

        <fieldset>
          <legend>Razza</legend>
          <label className="modulo__campo">
            <span>Razza</span>
            <select name="breed" defaultValue={dato?.breed?.id ?? ''}>
              <option value="">— nessuna —</option>
              {razze.data?.member.map((razza: BreedRead) => (
                <option key={razza.id} value={razza.id}>
                  {razza.name}
                </option>
              ))}
            </select>
          </label>
          {razze.isError && (
            <p role="alert" className="modulo__errore">
              Non è stato possibile leggere l'elenco delle razze.
            </p>
          )}
          {/*
            Se le razze fossero più di quante ne abbiamo chieste, l'elenco
            sarebbe monco e non si vedrebbe: chi non trova la sua razza
            penserebbe che manchi dal catalogo. Meglio dirlo.
          */}
          {razzeParziali && (
            <p className="modulo__errore">
              L'elenco mostra {razze.data.member.length} razze su{' '}
              {razze.data.totalItems}: le altre non sono state caricate.
            </p>
          )}
          <Campo nome="variety" etichetta="Varietà" predefinito={dato?.variety} violazioni={violazioni} />
        </fieldset>

        <fieldset>
          <legend>Registro</legend>
          <Campo nome="registry_number" etichetta="Numero di registro" predefinito={dato?.registry_number} violazioni={violazioni} />
          <Campo nome="registry_book" etichetta="Libro" predefinito={dato?.registry_book} violazioni={violazioni} />
          <Campo nome="registered_on" etichetta="Iscritto il" tipo="date" predefinito={dato?.registered_on} violazioni={violazioni} />
          <label className="modulo__interruttore">
            <input type="checkbox" name="foundation_stock" defaultChecked={dato?.foundation_stock ?? false} />
            Capostipite (senza genitori noti)
          </label>
        </fieldset>

        <fieldset>
          <legend>Allevatore</legend>
          {/* In modifica si mostra chi c'è già, finché non lo si cambia: un
              campo di ricerca vuoto sopra un dato esistente fa credere che il
              dato non ci sia. */}
          {modifica && !allevatoreToccato && dato?.breeder && (
            <p className="riga__secondario">Attuale: {dato.breeder.display_name}</p>
          )}
          <CercaPersona
            scelta={allevatore}
            onScegli={(persona) => {
              setAllevatoreToccato(true)
              setAllevatore(persona)
            }}
          />
        </fieldset>

        {/*
          Gli attributi della specie, raggruppati come li raggruppa il
          catalogo: «identificazione», «morfologia», «sanità». I nomi dei
          gruppi li decide l'associazione, quindi qui non si traducono né si
          riordinano — comparirebbero in un ordine che chi ha compilato il
          catalogo non riconosce.
        */}
        {raggruppa(attributi).map(([gruppo, elenco]) => (
          <fieldset key={gruppo}>
            <legend>{gruppo}</legend>
            {elenco.map((attributo) => (
              <CampoAttributo
                key={attributo.key}
                attributo={attributo}
                violazioni={violazioni}
                predefinito={dato?.attributes?.[attributo.key] ?? null}
              />
            ))}
          </fieldset>
        ))}

        <fieldset>
          <legend>Altro</legend>
          <label className="modulo__interruttore">
            <input type="checkbox" name="for_sale" defaultChecked={dato?.for_sale ?? false} />
            In vendita
          </label>
          <label className="modulo__campo modulo__campo--largo">
            <span>Note</span>
            <textarea name="notes" rows={3} defaultValue={dato?.notes ?? ''} />
          </label>
        </fieldset>

        <div className="modulo__azioni">
          <button type="button" data-variante="piatto" onClick={onAnnulla} disabled={salva.isPending}>
            Annulla
          </button>
          <button type="submit" disabled={salva.isPending}>
            {salva.isPending ? 'Salvataggio…' : modifica ? 'Salva le modifiche' : 'Registra'}
          </button>
        </div>
      </form>
    </section>
  )
}

/** Gli attributi per gruppo, nell'ordine in cui il catalogo li elenca. */
function raggruppa(attributi: readonly Attributo[]): [string, Attributo[]][] {
  const gruppi = new Map<string, Attributo[]>()

  for (const attributo of attributi) {
    const nome = attributo.group ?? 'Altri dati'

    gruppi.set(nome, [...(gruppi.get(nome) ?? []), attributo])
  }

  return [...gruppi.entries()]
}

/**
 * Un campo costruito dal tipo dichiarato nel catalogo.
 *
 * I tipi possibili sono otto e sono definiti dal backend
 * (`SpeciesAttribute::TYPE_*`). Quelli che il catalogo dimostrativo non usa
 * sono gestiti lo stesso: un'associazione può definirli domani, e un tipo non
 * previsto qui diventerebbe un campo di testo muto — accettato dal modulo e
 * rifiutato dal server, con un errore che non spiega perché.
 */
function CampoAttributo({
  attributo,
  violazioni,
  predefinito,
}: {
  attributo: Attributo
  violazioni: Record<string, string>
  predefinito?: unknown
}) {
  const nome = `attr:${attributo.key}`
  const percorso = `attributes.${attributo.key}`
  const errore = violazioni[percorso]
  const idErrore = `errore-${attributo.key}`
  const etichetta = attributo.unit ? `${attributo.label} (${attributo.unit})` : attributo.label

  // `toFieldValue` e non `String()`: il valore arriva dal catalogo come
  // `unknown`, e un oggetto inatteso finirebbe nel campo come
  // «[object Object]» — un dato falso che al salvataggio verrebbe scritto.
  // La funzione lo trasforma in un campo vuoto, cioè visibilmente mancante.
  const iniziale = toFieldValue(predefinito)

  if (attributo.type === 'boolean') {
    return (
      <label className="modulo__interruttore">
        <input type="checkbox" name={nome} defaultChecked={iniziale === 'true' || iniziale === '1'} />
        {etichetta}
      </label>
    )
  }

  const comuni = {
    name: nome,
    'aria-invalid': errore ? true : undefined,
    'aria-describedby': errore ? idErrore : undefined,
  } as const

  return (
    <label className="modulo__campo">
      <span>
        {etichetta}
        {attributo.required && <abbr title="obbligatorio"> *</abbr>}
      </span>

      {attributo.type === 'enum' ? (
        <select {...comuni} defaultValue={iniziale}>
          <option value="">— non indicato —</option>
          {(attributo.options ?? []).map((opzione) => (
            <option key={opzione} value={opzione}>
              {opzione}
            </option>
          ))}
        </select>
      ) : attributo.type === 'text' ? (
        <textarea {...comuni} rows={3} defaultValue={iniziale} />
      ) : (
        <input
          {...comuni}
          type={tipoDelCampo(attributo.type)}
          defaultValue={attributo.type === 'date' ? iniziale.slice(0, 10) : iniziale}
          {...modoNumerico(attributo.type)}
        />
      )}

      {errore && (
        <span className="modulo__errore" id={idErrore} role="alert">
          {errore}
        </span>
      )}
    </label>
  )
}

/**
 * I numeri restano campi di testo, con la tastiera numerica sui telefoni.
 *
 * `type="number"` sembrerebbe la scelta ovvia, ma il modo in cui tratta la
 * virgola dipende dal browser e dalla lingua del sistema: dove non la accetta,
 * `value` torna **vuoto** — il numero digitato sparisce senza un messaggio, e
 * il campo risulta non compilato. Con un campo di testo la virgola arriva
 * intera e la conversione la facciamo noi, allo stesso modo ovunque.
 */
function modoNumerico(tipo: string): { inputMode?: 'decimal' | 'numeric' } {
  if (tipo === 'decimal') return { inputMode: 'decimal' }
  if (tipo === 'integer') return { inputMode: 'numeric' }

  return {}
}

function tipoDelCampo(tipo: string): string {
  switch (tipo) {
    case 'date':
      return 'date'
    default:
      // `file` compreso: qui è il riferimento a un documento, non un
      // caricamento — il gestionale non ha ancora un endpoint per i file, e
      // un selettore di file che non carica niente sarebbe una bugia.
      return 'text'
  }
}

/**
 * La ricerca di una persona.
 *
 * Non è un menu a tendina: le persone in anagrafica sono migliaia, e un
 * elenco con tutte dentro è lento da caricare e inutile da scorrere. Si cerca
 * per cognome — l'unico filtro che il backend espone — e si sceglie.
 */
function CercaPersona({
  scelta,
  onScegli,
}: {
  scelta: PersonRead | null
  onScegli: (persona: PersonRead | null) => void
}) {
  const { resources } = useSession()
  const [testo, setTesto] = useState('')

  const risultati = useQuery({
    queryKey: ['people', 'cerca', testo],
    queryFn: ({ signal }) =>
      resources.people.list({ last_name: testo.trim(), items_per_page: 8 }, signal),
    enabled: testo.trim().length >= 2,
  })

  if (scelta) {
    return (
      <div className="scelta">
        <span className="riga__nome">{scelta.display_name}</span>
        <button
          type="button"
          data-variante="piatto"
          onClick={() => {
            onScegli(null)
            setTesto('')
          }}
        >
          Cambia
        </button>
      </div>
    )
  }

  return (
    <div>
      <label className="modulo__campo">
        <span>Cerca per cognome</span>
        <input
          type="search"
          value={testo}
          placeholder="Cognome o ragione sociale"
          onChange={(evento) => {
            setTesto(evento.target.value)
          }}
        />
      </label>

      {/* Sotto le due lettere non si cerca: una richiesta per ogni tasto
          premuto restituirebbe mezza anagrafica a ogni battuta. */}
      {testo.trim().length >= 2 && risultati.data?.member.length === 0 && (
        <p className="modulo__errore">Nessuna persona trovata.</p>
      )}

      {risultati.data && risultati.data.member.length > 0 && (
        <ul className="scelta__elenco">
          {risultati.data.member.map((persona) => (
            <li key={persona.id}>
              <button
                type="button"
                data-variante="piatto"
                onClick={() => {
                  onScegli(persona)
                }}
              >
                {persona.display_name}
                {persona.city && <span className="riga__secondario"> · {persona.city}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Campo({
  nome,
  etichetta,
  violazioni,
  tipo = 'text',
  predefinito,
  obbligatorio = false,
}: {
  nome: string
  etichetta: string
  violazioni: Record<string, string>
  tipo?: string
  predefinito?: string | null | undefined
  obbligatorio?: boolean
}) {
  const errore = violazioni[nome]
  const idErrore = `errore-${nome}`

  return (
    <label className="modulo__campo">
      <span>
        {etichetta}
        {obbligatorio && <abbr title="obbligatorio"> *</abbr>}
      </span>
      <input
        type={tipo}
        name={nome}
        // Le date arrivano come istanti e i campi `type="date"` accettano solo
        // `AAAA-MM-GG`: senza il taglio il campo resta vuoto e il salvataggio
        // cancellerebbe la data invece di conservarla.
        defaultValue={tipo === 'date' ? (predefinito ?? '').slice(0, 10) : (predefinito ?? '')}
        aria-invalid={errore ? true : undefined}
        aria-describedby={errore ? idErrore : undefined}
      />
      {errore && (
        <span className="modulo__errore" id={idErrore} role="alert">
          {errore}
        </span>
      )}
    </label>
  )
}
